// ADV-1347 (lifecycle epic): one shared config of lifecycle stages per entity
// family. Lifts the icon/tint/label resolution out of the timeline component's
// hardcoded DISPATCH map so the generic LifecycleTimeline stays a dumb
// renderer (it never imports this config — the deriver/adapter resolves each
// event's marker first; see lifecycle-timeline.tsx).
//
// The config is keyed by the server's stage / event_type vocabulary. Because
// each entity family has its own disjoint stage enum (ADR-0026: ops.job_stage
// vs core.opportunity_stage), the icon/tint map MUST be parametrized per
// entity — never unified.
//
// Tints are the existing entity-agnostic semantic tokens (ADR-0019 §"faded,
// not flagged"): color lives only on the marker, never the chrome. No per-
// stage rainbow dots.

import type { LifecycleEntityType } from "@adva/types";

export type LifecycleTint =
  | "accent"
  | "success"
  | "muted"
  | "warning"
  | "danger";

// Stable keys mapped to a lucide icon inside the timeline. Keeping this an
// enum (rather than passing a component) lets the config stay free of React/
// lucide imports and keeps the resolved marker serializable.
export type LifecycleIconKey =
  | "created"
  | "scheduled"
  | "started"
  | "completed"
  | "signed_off"
  | "canceled"
  | "proposal"
  | "invoice"
  | "won"
  | "lost";

// Internal config shapes (not exported — nothing outside this module consumes
// them yet; a future per-entity tracker can re-export when it needs the type).
interface LifecycleStage {
  /** Enum value, e.g. "new" | "scheduled" | "in_progress". */
  key: string;
  /** Display label, e.g. "New", "Scheduled", "In Progress". */
  label: string;
  /** Resolves to a lucide icon in the timeline. */
  iconKey: LifecycleIconKey;
  /** Marker tint token. */
  tint: LifecycleTint;
}

interface LifecycleEntityConfig {
  /** The happy path — feeds the stage tracker's positional order. */
  primaryStages: LifecycleStage[];
  /** Terminal off-ramps (canceled / closed_lost) — feeds the ALT_TERMINAL node. */
  terminalStages: LifecycleStage[];
}

// Slice 1 ships the `job` config (the proving ground). `invoice` and
// `opportunity` are defined so the contract is generic-ready; their dedicated
// stage trackers keep their own bespoke rendering for now (a separate ticket
// migrates those). The job-stage-tracker consumes `LIFECYCLE_CONFIG.job`.
export const LIFECYCLE_CONFIG: Record<
  LifecycleEntityType,
  LifecycleEntityConfig
> = {
  job: {
    primaryStages: [
      { key: "new", label: "New", iconKey: "created", tint: "accent" },
      {
        key: "scheduled",
        label: "Scheduled",
        iconKey: "scheduled",
        tint: "accent",
      },
      {
        key: "in_progress",
        label: "In Progress",
        iconKey: "started",
        tint: "accent",
      },
      {
        key: "completed",
        label: "Completed",
        iconKey: "completed",
        tint: "success",
      },
    ],
    terminalStages: [
      {
        key: "canceled",
        label: "Canceled",
        iconKey: "canceled",
        tint: "muted",
      },
    ],
  },
  opportunity: {
    primaryStages: [
      { key: "lead", label: "Lead", iconKey: "created", tint: "accent" },
      {
        key: "appointment",
        label: "Appointment",
        iconKey: "scheduled",
        tint: "accent",
      },
      {
        key: "proposal",
        label: "Proposal Sent",
        iconKey: "proposal",
        tint: "accent",
      },
      {
        key: "closed_won",
        label: "Closed Won",
        iconKey: "won",
        tint: "success",
      },
    ],
    terminalStages: [
      {
        key: "closed_lost",
        label: "Closed Lost",
        iconKey: "lost",
        tint: "muted",
      },
    ],
  },
  invoice: {
    primaryStages: [
      { key: "draft", label: "Draft", iconKey: "created", tint: "accent" },
      { key: "sent", label: "Sent", iconKey: "invoice", tint: "accent" },
      { key: "partial", label: "Partial", iconKey: "invoice", tint: "warning" },
      { key: "paid", label: "Paid", iconKey: "completed", tint: "success" },
    ],
    terminalStages: [
      {
        key: "cancelled",
        label: "Cancelled",
        iconKey: "canceled",
        tint: "muted",
      },
    ],
  },
};

/**
 * Resolve the marker (iconKey + tint) for a lifecycle event. Looks up by the
 * event's `event_type` first (the milestone verb the view emits — created /
 * scheduled / started / completed / signed_off), falling back to the `stage`
 * key, then to a neutral accent marker for any unmapped value (forward-compat
 * so a new server milestone never renders blank).
 */
export function resolveLifecycleMarker(
  entityType: LifecycleEntityType,
  eventType: string,
  stage: string,
): { iconKey: LifecycleIconKey; tint: LifecycleTint } {
  // event_type → iconKey is a stable cross-entity vocabulary; map it directly.
  const byEventType: Partial<
    Record<string, { iconKey: LifecycleIconKey; tint: LifecycleTint }>
  > = {
    created: { iconKey: "created", tint: "accent" },
    scheduled: { iconKey: "scheduled", tint: "accent" },
    started: { iconKey: "started", tint: "accent" },
    completed: { iconKey: "completed", tint: "success" },
    signed_off: { iconKey: "completed", tint: "success" },
    canceled: { iconKey: "canceled", tint: "muted" },
    proposal: { iconKey: "proposal", tint: "accent" },
    invoice: { iconKey: "invoice", tint: "accent" },
    won: { iconKey: "won", tint: "success" },
    lost: { iconKey: "lost", tint: "muted" },
  };
  const direct = byEventType[eventType];
  if (direct) return direct;

  // Fall back to the stage definition in the entity config.
  const config = LIFECYCLE_CONFIG[entityType];
  const stageDef =
    config.primaryStages.find((s) => s.key === stage) ??
    config.terminalStages.find((s) => s.key === stage);
  if (stageDef) return { iconKey: stageDef.iconKey, tint: stageDef.tint };

  return { iconKey: "created", tint: "accent" };
}
